import { useEffect, useRef, type ReactNode } from "react";
import type { EditorView } from "prosemirror-view";

import { cx } from "../../lib/cx";

export interface Placement {
  top: number;
  left: number;
}

/**
 * Where a menu goes: just under a document position, in viewport coordinates.
 * jsdom reports zeroes for every rectangle, which is why the value is only ever
 * used for styling and never for behaviour.
 */
export function placementAtPos(view: EditorView, pos: number, offset = 6): Placement {
  try {
    const coords = view.coordsAtPos(Math.max(0, Math.min(pos, view.state.doc.content.size)));
    return { top: coords.bottom + offset, left: coords.left };
  } catch {
    return { top: 0, left: 0 };
  }
}

/** Above the selection, which is where the selection toolbar belongs (§3). */
export function placementAboveSelection(view: EditorView, offset = 8): Placement {
  try {
    const start = view.coordsAtPos(view.state.selection.from);
    const end = view.coordsAtPos(view.state.selection.to);
    return { top: start.top - offset, left: Math.min(start.left, end.left) };
  } catch {
    return { top: 0, left: 0 };
  }
}

export interface MenuPopoverProps {
  placement: Placement;
  role?: "menu" | "listbox" | "toolbar" | "dialog";
  label: string;
  className?: string;
  onDismiss?: () => void;
  children: ReactNode;
  testId?: string;
}

/**
 * The shell every editor menu shares: fixed position, a role and a label for
 * assistive technology, Escape to dismiss, and a click-outside guard. Focus is
 * left where it is — in the document — because these menus float beside a live
 * caret; the ones that take the keyboard do it through the suggestion bridge.
 */
export function MenuPopover({
  placement,
  role = "menu",
  label,
  className,
  onDismiss,
  children,
  testId,
}: MenuPopoverProps): ReactNode {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onDismiss) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onDismiss();
    };
    const onPointerDown = (event: MouseEvent): void => {
      if (event.target instanceof Node && ref.current?.contains(event.target) === true) return;
      onDismiss();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [onDismiss]);

  return (
    <div
      ref={ref}
      role={role}
      aria-label={label}
      data-testid={testId}
      className={cx("wiki-menu", className)}
      style={{ top: `${String(placement.top)}px`, left: `${String(placement.left)}px` }}
    >
      {children}
    </div>
  );
}

export interface ToolbarButtonProps {
  label: string;
  onSelect: () => void;
  active?: boolean;
  disabled?: boolean;
  children?: ReactNode;
}

/**
 * One control in a toolbar. Toolbars use roving focus: every button is
 * reachable with the arrow keys, and only one is in the tab order.
 */
export function ToolbarButton({
  label,
  onSelect,
  active = false,
  disabled = false,
  children,
}: ToolbarButtonProps): ReactNode {
  return (
    <button
      type="button"
      className={cx("wiki-menu__button", active && "wiki-menu__button--active")}
      aria-label={label}
      title={label}
      aria-pressed={active}
      disabled={disabled}
      // The editor keeps the selection: a toolbar press must not blur it.
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={onSelect}
    >
      {children ?? label}
    </button>
  );
}

/** Arrow-key navigation within a toolbar (`editor.md` accessibility notes). */
export function useRovingFocus(containerRef: React.RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
      const buttons = Array.from(container.querySelectorAll<HTMLButtonElement>("button"));
      if (buttons.length === 0) return;
      const index = buttons.findIndex((button) => button === document.activeElement);
      const next =
        event.key === "ArrowRight"
          ? (index + 1) % buttons.length
          : (index - 1 + buttons.length) % buttons.length;
      buttons[next]?.focus();
      event.preventDefault();
    };
    container.addEventListener("keydown", onKeyDown);
    return () => {
      container.removeEventListener("keydown", onKeyDown);
    };
  }, [containerRef]);
}

export function Separator(): ReactNode {
  return <span className="wiki-menu__separator" role="separator" />;
}
